import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { ProcessChartProps } from './types';

/**
 * RouteMapChart — 공종별 진행 '노선도'(스네이크 경로) SVG. 공정 현황 차트의 한 형태.
 * 기획안(고객리포트_설정.html) initMap/render 로직 포팅. 색은 표준 토큰(var(--...))만 사용.
 * 반응형: 컨테이너 폭에 따라 열 수(2~8)를 바꿔 되그린다(ResizeObserver).
 *
 * ※ 차트 교체: 이 파일을 바꾸지 말고, ./index.ts 의 `export { ... as ProcessChart }` 대상만 바꾸면 된다.
 */

// 색 토큰(기획안 --color-*/--t* → 앱 셸 토큰 매핑)
const C_TRACK = 'var(--shell-border)';
const C_DONE = 'var(--color-success)';
const C_ACTIVE = 'var(--color-info)';
const C_ACTIVE_DARK = 'var(--color-infoStrong)';
const C_ACTIVE_SUBTLE = 'var(--color-infoSubtle)';
const C_CARD = 'var(--shell-surface)';
const C_ON_PRIMARY = 'var(--gt-on-primary)';
const C_T1 = 'var(--shell-on-surface)';
const C_T2 = 'var(--shell-on-surface-2)';
const C_T3 = 'var(--shell-hint)';

function getColumns(width: number): number {
  if (width >= 1100) return 8;
  if (width >= 960) return 7;
  if (width >= 680) return 6;
  if (width >= 560) return 5;
  if (width >= 420) return 4;
  if (width >= 300) return 3;
  return 2;
}

export function RouteMapChart({ steps }: ProcessChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () => setWidth(el.clientWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div ref={containerRef} className="w-full">
      {width > 0 && <RouteSvg steps={steps} width={width - 16} />}
    </div>
  );
}

function RouteSvg({ steps, width }: { steps: ProcessChartProps['steps']; width: number }) {
  const cols = getColumns(width);
  const curveOffset = 42;
  const padX = curveOffset + 16;
  const padTop = 28;
  const rowH = 112;
  const curveR = 34;
  const availW = width - padX * 2;
  const gapX = cols > 1 ? availW / (cols - 1) : 0;
  const totalRows = Math.ceil(steps.length / cols);
  const svgH = padTop + totalRows * rowH - 32;

  // 스네이크 좌표
  const coords = steps.map((step, i) => {
    const row = Math.floor(i / cols);
    const posInRow = i % cols;
    const reversed = row % 2 === 1;
    const col = reversed ? cols - 1 - posInRow : posInRow;
    return { ...step, row, col, x: padX + col * gapX, y: padTop + row * rowH + 34 };
  });

  const buildSubPath = (startIdx: number, endIdx: number): string => {
    let d = '';
    for (let i = startIdx; i <= endIdx && i < coords.length; i++) {
      const c = coords[i];
      if (i === startIdx) {
        d += `M ${c.x} ${c.y}`;
        continue;
      }
      const p = coords[i - 1];
      if (p.row === c.row) {
        d += ` L ${c.x} ${c.y}`;
      } else {
        const curveRight = p.row % 2 === 0;
        const sign = curveRight ? 1 : -1;
        const ex = p.x + sign * curveOffset;
        const r = Math.min(curveR, Math.abs(c.y - p.y) / 2);
        d += ` Q ${ex} ${p.y} ${ex} ${p.y + r}`;
        d += ` L ${ex} ${c.y - r}`;
        d += ` Q ${ex} ${c.y} ${c.x} ${c.y}`;
      }
    }
    return d;
  };

  const fullPath = buildSubPath(0, coords.length - 1);
  let lastDoneIdx = -1;
  let lastActiveIdx = -1;
  steps.forEach((s, i) => {
    if (s.status === 'done') lastDoneIdx = i;
    if (s.status === 'active') lastActiveIdx = i;
  });
  const activePath = lastActiveIdx >= 0 ? buildSubPath(0, lastActiveIdx) : '';
  const greenPath = lastDoneIdx >= 0 ? buildSubPath(0, lastDoneIdx) : '';
  const allDone = steps.every((s) => s.status === 'done' || s.status === 'finish');

  const nodes: ReactNode[] = [];
  coords.forEach((c, i) => {
    const step = steps[i];
    const x = c.x;
    const y = c.y;
    let labelColor = C_T3;
    let daysColor = C_T3;
    if (step.status === 'done') {
      labelColor = daysColor = C_DONE;
    } else if (step.status === 'active') {
      labelColor = daysColor = C_ACTIVE_DARK;
    } else if (step.status === 'finish') {
      labelColor = daysColor = C_T1;
    }

    // 공종명
    nodes.push(
      <text key={`n${i}`} x={x} y={y - 22} textAnchor="middle" fill={labelColor} fontSize={13} fontWeight={500}>
        {step.name}
      </text>,
    );

    // 노드
    if (step.status === 'done') {
      nodes.push(<circle key={`c${i}`} cx={x} cy={y} r={13} fill={C_DONE} stroke={C_CARD} strokeWidth={2.5} />);
      nodes.push(
        <text key={`k${i}`} x={x} y={y + 4} textAnchor="middle" fill={C_ON_PRIMARY} fontSize={10} fontWeight={500}>
          ✓
        </text>,
      );
    } else if (step.status === 'active') {
      nodes.push(
        <circle key={`a1${i}`} cx={x} cy={y} r={14} fill={C_ACTIVE} opacity={0}>
          <animate attributeName="r" values="16;30" dur="1.5s" repeatCount="indefinite" begin={`${i * 0.25}s`} />
          <animate attributeName="opacity" values="0.4;0" dur="1.5s" repeatCount="indefinite" begin={`${i * 0.25}s`} />
        </circle>,
      );
      nodes.push(
        <circle key={`a2${i}`} cx={x} cy={y} r={14} fill={C_ACTIVE_SUBTLE} stroke={C_ACTIVE} strokeWidth={3}>
          <animate attributeName="stroke-width" values="3;7;3" dur="1.5s" repeatCount="indefinite" begin={`${i * 0.25}s`} />
          <animate attributeName="r" values="14;16;14" dur="1.5s" repeatCount="indefinite" begin={`${i * 0.25}s`} />
        </circle>,
      );
    } else if (step.status === 'finish') {
      if (allDone) {
        nodes.push(<circle key={`f${i}`} cx={x} cy={y} r={13} fill={C_DONE} stroke={C_T1} strokeWidth={2.5} />);
        nodes.push(
          <text key={`fk${i}`} x={x} y={y + 4} textAnchor="middle" fill={C_ON_PRIMARY} fontSize={10} fontWeight={500}>
            ✓
          </text>,
        );
      } else {
        nodes.push(<circle key={`f${i}`} cx={x} cy={y} r={11} fill={C_CARD} stroke={C_T1} strokeWidth={2.5} />);
      }
    } else {
      nodes.push(<circle key={`p${i}`} cx={x} cy={y} r={11} fill={C_CARD} stroke={C_TRACK} strokeWidth={2.5} />);
    }

    // 일자 / 기간
    nodes.push(
      <text key={`d${i}`} x={x} y={y + 30} textAnchor="middle" fill={C_T2} fontSize={11}>
        {step.date}
      </text>,
    );
    if (step.days) {
      nodes.push(
        <text key={`y${i}`} x={x} y={y + 44} textAnchor="middle" fill={daysColor} fontSize={11.5} fontWeight={500}>
          {step.days}
        </text>,
      );
    }
  });

  return (
    <svg viewBox={`0 0 ${width} ${svgH}`} style={{ width: '100%', height: 'auto' }} xmlns="http://www.w3.org/2000/svg">
      <path d={fullPath} fill="none" stroke={C_TRACK} strokeWidth={5} strokeLinecap="round" strokeLinejoin="round" />
      {activePath && (
        <path d={activePath} fill="none" stroke={C_ACTIVE} strokeWidth={5} strokeLinecap="round" strokeLinejoin="round" />
      )}
      {greenPath && <path d={greenPath} fill="none" stroke={C_DONE} strokeWidth={5} strokeLinecap="round" />}
      {nodes}
    </svg>
  );
}
